import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Play,
  Home,
  Sparkles,
  Flame,
  BookOpen,
  GraduationCap,
  Timer,
  Trophy,
  TrendingUp,
  CheckCircle,
  Clock,
  Zap,
  Rocket,
  ArrowUpRight,
  Brain,
  Target,
  Medal,
  Star,
  Activity,
  FlaskConical,
  ChevronRight,
  Lightbulb,
} from 'lucide-react';
import {
  getGreeting,
  getProgress,
  getFocusEnrollment,
  getPortalInsight,
  getWeeklyMomentum,
  buildActivityFeed,
  getEarnedAchievements,
} from './studentPortalUtils';

const ACHIEVEMENT_ICONS = {
  book: BookOpen,
  cap: GraduationCap,
  flame: Flame,
  star: Star,
  flask: FlaskConical,
  target: Target,
  medal: Medal,
};

const StudentPortalDashboard = ({
  studentInfo,
  stats,
  enrollments,
  searchTerm,
  onTabChange,
  getCategoryIcon,
  pickColor,
}) => {
  const navigate = useNavigate();
  const greeting = getGreeting();
  const focus = getFocusEnrollment(enrollments);
  const insight = getPortalInsight({ stats, focusEnrollment: focus, studentName: studentInfo.name });
  const weekly = getWeeklyMomentum(stats.totalHours);
  const activity = buildActivityFeed(enrollments, stats);
  const achievements = getEarnedAchievements(stats);

  const level = Math.floor(stats.achievementPoints / 500) + 1;
  const nextLevelPoints = level * 500;
  const levelProgress = ((stats.achievementPoints % 500) / 500) * 100;

  const goFocus = () => {
    if (focus?.course?._id) {
      navigate(`/studentportal/mycourses/${focus.course._id}`);
    } else if (insight.path) {
      navigate(insight.path);
    } else {
      navigate('/courses');
    }
  };

  const quickActions = [
    {
      label: 'Continue',
      desc: focus ? `Resume ${focus.course?.title?.slice(0, 28)}…` : 'Pick a course to begin',
      icon: <Play size={18} />,
      color: '#34d399',
      disabled: !focus,
      action: goFocus,
    },
    {
      label: 'Qore AI',
      desc: 'Unblock concepts in seconds',
      icon: <Brain size={18} />,
      color: '#8b5cf6',
      action: () => navigate('/aitutoring'),
    },
    {
      label: 'Virtual Labs',
      desc: 'Run simulations & experiments',
      icon: <FlaskConical size={18} />,
      color: '#22d3ee',
      action: () => navigate('/virtuallabs'),
    },
    {
      label: 'Learning Path',
      desc: 'AI roadmap for your goals',
      icon: <Target size={18} />,
      color: '#f59e0b',
      action: () => navigate('/create-path'),
    },
  ];

  const activeCourses = enrollments.filter((e) => e.status !== 'completed');

  return (
    <div className="tab-content sp-dashboard">
      {/* Insight + hero bento */}
      <section className="sp-bento sp-bento--hero">
        <div className="sp-insight sp-insight--hero">
          <div className="sp-insight__badge">
            <Lightbulb size={14} />
            <span>Today's insight</span>
          </div>
          <h2 className="sp-insight__title">{insight.headline}</h2>
          <p className="sp-insight__body">{insight.body}</p>
          <button type="button" className="sp-btn sp-btn--primary" onClick={goFocus}>
            {insight.cta}
            <ArrowUpRight size={16} />
          </button>
        </div>

        <div className="sp-hero-card">
          <div className="sp-hero-card__greet">
            <span className="sp-hero-card__pill">{greeting}, {studentInfo.name.split(' ')[0]}</span>
            <h1>
              Build skills that <span className="gradient-text">ship in the real world</span>
            </h1>
            <p>
              {stats.totalCourses > 0
                ? `${stats.coursesInProgress} active · ${stats.coursesCompleted} done · Level ${level}`
                : 'Engineering education that adapts to you — courses, labs, and AI in one cockpit.'}
            </p>
          </div>
          <div className="sp-hero-card__level">
            <div className="sp-level-ring" style={{ '--pct': levelProgress }}>
              <span className="sp-level-ring__value">L{level}</span>
            </div>
            <div>
              <div className="sp-hero-card__xp">{stats.achievementPoints} / {nextLevelPoints} XP</div>
              <div className="sp-hero-card__streak">
                <Flame size={16} />
                {stats.currentStreak}-day streak
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Focus + weekly momentum */}
      <section className="sp-bento sp-bento--focus">
        <article className="sp-focus-card">
          <div className="sp-focus-card__head">
            <span className="sp-focus-card__label">Today's focus</span>
            {focus && (
              <span className="sp-focus-card__pct">{getProgress(focus)}%</span>
            )}
          </div>
          {focus ? (
            <>
              <h3>{focus.course?.title}</h3>
              <p>
                {focus.progress?.currentLessonTitle
                  ? `Up next: ${focus.progress.currentLessonTitle}`
                  : `With ${focus.course?.instructor?.name || 'your instructor'}`}
              </p>
              <div className="sp-focus-card__bar">
                <div className="sp-focus-card__fill" style={{ width: `${getProgress(focus)}%` }} />
              </div>
              <div className="sp-focus-card__actions">
                <button type="button" className="sp-btn sp-btn--primary" onClick={goFocus}>
                  <Play size={16} />
                  Resume session
                </button>
                <button type="button" className="sp-btn sp-btn--ghost" onClick={() => onTabChange('courses')}>
                  All courses
                </button>
              </div>
            </>
          ) : (
            <>
              <h3>No active session yet</h3>
              <p>Choose a course and we'll surface your next best action here every day.</p>
              <button type="button" className="sp-btn sp-btn--primary" onClick={() => navigate('/courses')}>
                <BookOpen size={16} />
                Find a course
              </button>
            </>
          )}
        </article>

        <article className="sp-momentum-card">
          <div className="sp-momentum-card__head">
            <Timer size={18} />
            <div>
              <h3>Weekly momentum</h3>
              <p>Goal: {weekly.goalHours}h of deep work</p>
            </div>
            <strong>{weekly.pct}%</strong>
          </div>
          <div className="sp-momentum-card__bar">
            <div className="sp-momentum-card__fill" style={{ width: `${weekly.pct}%` }} />
          </div>
          <div className="sp-momentum-days">
            {weekly.days.map((d, i) => (
              <span
                key={`${d}-${i}`}
                className={`sp-momentum-day ${i <= weekly.todayIndex ? 'sp-momentum-day--on' : ''} ${i === weekly.todayIndex ? 'sp-momentum-day--today' : ''}`}
              >
                {d}
              </span>
            ))}
          </div>
          <p className="sp-momentum-card__foot">
            {stats.totalHours}h logged total · {Math.max(0, weekly.goalHours - weekly.current).toFixed(1)}h to weekly goal
          </p>
        </article>

        <Link to="/aitutoring" className="sp-ai-nudge">
          <div className="sp-ai-nudge__icon">
            <Sparkles size={22} />
          </div>
          <div>
            <h3>Stuck on a concept?</h3>
            <p>Qore AI knows your courses and can debug, explain, or quiz you — right now.</p>
          </div>
          <ArrowUpRight size={18} />
        </Link>
      </section>

      {/* Stats — honest, data-driven */}
      <section className="sp-stats-bento">
        {[
          { key: 'active', value: stats.coursesInProgress, label: 'Active courses', icon: BookOpen, hint: stats.coursesInProgress ? 'In flight' : 'Add one', color: '#22d3ee' },
          { key: 'done', value: stats.coursesCompleted, label: 'Completed', icon: GraduationCap, hint: stats.coursesCompleted ? 'Shipped' : 'Pending', color: '#34d399' },
          { key: 'hours', value: `${stats.totalHours}h`, label: 'Study time', icon: Timer, hint: 'All-time', color: '#8b5cf6' },
          { key: 'xp', value: stats.achievementPoints.toLocaleString(), label: 'XP earned', icon: Trophy, hint: `Level ${level}`, color: '#f59e0b' },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.key} className="sp-stat-tile" style={{ '--tile-color': s.color }}>
              <Icon size={20} />
              <div className="sp-stat-tile__value">{s.value}</div>
              <div className="sp-stat-tile__label">{s.label}</div>
              <span className="sp-stat-tile__hint">{s.hint}</span>
            </div>
          );
        })}
      </section>

      {/* Quick launch */}
      <section className="sp-quick-launch">
        <h3><Rocket size={18} /> Launch pad</h3>
        <div className="sp-quick-launch__grid">
          {quickActions.map((action) => (
            <button
              key={action.label}
              type="button"
              className="sp-launch-card"
              style={{ '--launch-color': action.color }}
              onClick={action.action}
              disabled={action.disabled}
            >
              <div className="sp-launch-card__icon">{action.icon}</div>
              <div>
                <strong>{action.label}</strong>
                <span>{action.desc}</span>
              </div>
              <ArrowUpRight size={16} />
            </button>
          ))}
        </div>
      </section>

      {/* Main grid */}
      <section className="sp-bento sp-bento--main">
        <article className="portal-card sp-panel sp-panel--wide">
          <div className="card-header">
            <h3 className="card-title"><BookOpen size={18} /> Continue learning</h3>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onTabChange('courses')}>
              View all <ChevronRight size={14} />
            </button>
          </div>
          {activeCourses.length === 0 ? (
            <div className="sp-empty sp-empty--inline">
              <div className="sp-empty__art">📐</div>
              <div>
                <h4>Your shelf is empty</h4>
                <p>Start with AI, green tech, or core engineering — we'll track every lesson here.</p>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/courses')}>
                  Browse catalog
                </button>
              </div>
            </div>
          ) : (
            <div className="course-list">
              {activeCourses.slice(0, 3).map((enrollment) => {
                const progress = getProgress(enrollment);
                const color = pickColor(enrollment.course?.category || enrollment.course?.title || '');
                return (
                  <div key={enrollment._id} className="course-item-enhanced">
                    <div className="course-item-left">
                      <div className="course-icon-enhanced" style={{ background: `${color}20`, color }}>
                        {getCategoryIcon(enrollment.course?.category)}
                      </div>
                      <div className="course-details">
                        <h4 className="course-title-enhanced">{enrollment.course?.title}</h4>
                        <p className="course-meta">
                          {enrollment.course?.instructor?.name || 'Instructor'} · {progress}% · est. {Math.max(1, Math.round((100 - progress) / 10))} sessions left
                        </p>
                        <div className="progress-bar-slim">
                          <div className="progress-fill-slim" style={{ width: `${progress}%`, background: color }} />
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-continue"
                      style={{ background: color }}
                      onClick={() => navigate(`/studentportal/mycourses/${enrollment.course._id}`)}
                    >
                      <Play size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </article>

        <article className="portal-card sp-panel">
          <div className="card-header">
            <h3 className="card-title"><Activity size={18} /> Pulse</h3>
          </div>
          <div className="activity-list">
            {activity.map((item, idx) => (
              <div key={idx} className="activity-item">
                <div className="activity-icon" style={{ background: `${item.color}20`, color: item.color }}>
                  {item.kind === 'complete' ? <CheckCircle size={14} /> : item.kind === 'milestone' ? <TrendingUp size={14} /> : <Clock size={14} />}
                </div>
                <div className="activity-content">
                  <p className="activity-text">{item.text}</p>
                  <span className="activity-time">{item.time}</span>
                </div>
              </div>
            ))}
          </div>
        </article>

        <article className="portal-card sp-panel sp-panel--path">
          <div className="path-preview">
            <div className="path-icon-container">
              <Brain size={32} />
              <div className="pulse-ring" />
            </div>
            <h4 className="path-title">AI learning path</h4>
            <p className="path-description">
              Tell us your target role — we'll sequence courses, labs, and milestones automatically.
            </p>
            <button type="button" className="btn btn-primary btn-full" onClick={() => navigate('/create-path')}>
              <Sparkles size={16} />
              Design my path
            </button>
          </div>
        </article>

        <article className="portal-card sp-panel">
          <div className="card-header">
            <h3 className="card-title"><Trophy size={18} /> Badges</h3>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onTabChange('achievements')}>
              All <ChevronRight size={14} />
            </button>
          </div>
          <div className="achievements-preview sp-badges-grid">
            {achievements.slice(0, 6).map((a) => {
              const Icon = ACHIEVEMENT_ICONS[a.icon] || Star;
              return (
                <div key={a.id} className={`achievement-badge ${a.earned ? 'earned' : 'locked'}`}>
                  <Icon size={22} />
                  <span>{a.label}</span>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      {searchTerm?.trim() && (
        <div className="sp-search-callout">
          <p>Searching for “{searchTerm}” — open <button type="button" className="sp-link-btn" onClick={() => onTabChange('courses')}>My Courses</button> to see matches.</p>
        </div>
      )}

      <section className="sp-labs-banner">
        <FlaskConical size={28} />
        <div>
          <h3>Virtual labs — learn by doing</h3>
          <p>Medical statistics, physics mechanics, and more. Generate keys from enrolled courses.</p>
        </div>
        <button type="button" className="sp-btn sp-btn--primary" onClick={() => navigate('/virtuallabs')}>
          Open labs
        </button>
      </section>
    </div>
  );
};

export default StudentPortalDashboard;